import { months } from "../Helpers";
import store from "../../store";

export function getBookingInstallDetails(booking) {
  if (!booking) return {};
  if (booking.installDetails) return booking.installDetails;
  if (booking.booking?.installDetails) return booking.booking.installDetails;
  if (booking.customEvent?.serviceDetails) return booking.customEvent.serviceDetails;
  if (booking.serviceDetails) return booking.serviceDetails;
  return {};
}

export function getBookingVehicleDetails(booking) {
  if (!booking) return {};
  return (
    booking.vehicleDetails ||
    booking.booking?.vehicleDetails ||
    booking.customEvent?.serviceDetails?.vehicleDetails ||
    {}
  );
}

function getMonthIndex(monthName) {
  if (!monthName) return -1;
  const idx = months.findIndex(
    (m) => m.toLowerCase() === String(monthName).trim().toLowerCase()
  );
  return idx;
}

/** @returns {Date|null} */
export function parseBookingDate(booking) {
  const details = getBookingInstallDetails(booking);
  if (details.selectedYear && details.selectedMonth != null && details.selectedDay != null) {
    const mi = getMonthIndex(details.selectedMonth);
    if (mi < 0) return null;
    const day = parseInt(String(details.selectedDay).replace(/^0+/, "") || "0", 10);
    const year = parseInt(String(details.selectedYear), 10);
    if (!day || !year) return null;
    const d = new Date(year, mi, day);
    d.setHours(23, 59, 59, 999);
    return d;
  }
  if (details.startTime && typeof details.startTime === "number") {
    return new Date(details.startTime * 1000);
  }
  return null;
}

export function isBookingCancelledOrNoShow(booking) {
  const details = getBookingInstallDetails(booking);
  const vehicle = getBookingVehicleDetails(booking);
  return !!(
    booking?.cancelled ||
    vehicle?.status === "cancelled" ||
    details?.isNoShow ||
    details?.status === "Cancelled" ||
    details?.status === "No show"
  );
}

export function isBookingInPast(booking) {
  const d = parseBookingDate(booking);
  if (!d) return false;
  return d.getTime() < Date.now();
}

export function isBookingInFuture(booking) {
  const d = parseBookingDate(booking);
  if (!d) return false;
  return d.getTime() > Date.now();
}

/** Schedule a warranty return visit — past jobs, or on file without a parseable date. */
export function isBookingEligibleForWarrantyVisit(booking) {
  if (!booking) return false;
  if (isBookingCancelledOrNoShow(booking)) return false;
  if (isBookingInFuture(booking)) return false;
  if (isBookingInPast(booking)) return true;
  return parseBookingDate(booking) === null;
}

/** Create a film warranty certificate for a prior install — any non-cancelled job not in the future. */
export function isBookingEligibleForFilmWarranty(booking) {
  if (!booking) return false;
  if (isBookingCancelledOrNoShow(booking)) return false;
  return !isBookingInFuture(booking);
}

/** @deprecated Use isBookingEligibleForWarrantyVisit */
export function isBookingEligibleForWarranty(booking) {
  return isBookingEligibleForWarrantyVisit(booking);
}

export function getBookingDisplayStatus(booking) {
  if (isBookingCancelledOrNoShow(booking)) {
    const details = getBookingInstallDetails(booking);
    if (details?.isNoShow || details?.status === "No show") return "No Show";
    return "Cancelled";
  }
  if (isBookingInPast(booking)) return "Completed";
  return "Active";
}

export function formatCalendarEventTitle(installDetails, vehicleDetails) {
  const vehicleType =
    vehicleDetails?.vehicleType || installDetails?.vehicleType || "Vehicle";
  const installType =
    installDetails?.installType || installDetails?.serviceName || "Service";
  const base = `${vehicleType} - ${installType}`;
  return installDetails?.isWarranty ? `Warranty · ${base}` : base;
}

/**
 * Build film-warranty certificate payload from a customer-profile booking row.
 */
/**
 * Minimal customer-profile booking shape from a warranty doc (for scheduling a warranty visit).
 */
export function buildRelatedBookingFromWarranty(warranty) {
  if (!warranty) return null;
  const service = warranty.service || {};
  const vehicle = warranty.vehicle || {};
  let selectedYear;
  let selectedMonth;
  let selectedDay;
  if (warranty.bookingDate) {
    const raw = String(warranty.bookingDate).trim();
    const d = raw.length === 10 && !raw.includes("T")
      ? new Date(`${raw}T12:00:00`)
      : new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      selectedYear = String(d.getFullYear());
      selectedMonth = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
      ][d.getMonth()];
      selectedDay = String(d.getDate()).padStart(2, "0");
    }
  }
  return {
    booking: {
      installDetails: {
        selectedYear,
        selectedMonth,
        selectedDay,
        startTime: warranty.bookingStartTime || "",
        serviceType: service.type || "",
        installType: service.installType || "",
        brandSerieName: service.filmBrand || service.filmSeries || "",
        filmSeries: service.filmSeries || "",
        shades: { ...(service.shades || {}) },
        isWarranty: false,
      },
      vehicleDetails: {
        make: vehicle.make || "",
        model: vehicle.model || "",
        year: vehicle.year || "",
        vehicleType: vehicle.type || vehicle.vehicleType || "",
        vin: vehicle.vin || "",
      },
    },
  };
}

export function buildWarrantyCertificateData(bookingId, booking, customer) {
  const install = getBookingInstallDetails(booking);
  const vehicle = getBookingVehicleDetails(booking);
  const cust = customer || {};
  let bookingDate = "";
  if (install.selectedYear && install.selectedMonth != null && install.selectedDay != null) {
    const mi = getMonthIndex(install.selectedMonth) + 1;
    const day = String(install.selectedDay).padStart(2, "0");
    bookingDate = `${install.selectedYear}-${String(mi).padStart(2, "0")}-${day}`;
  }
  return {
    id: String(bookingId || ""),
    customerName: cust.name || "",
    customerPhone: cust.phoneNumber || "",
    customerEmail: cust.email || "",
    vehicleMake: vehicle.make || "",
    vehicleModel: vehicle.model || "",
    vehicleYear: vehicle.year || "",
    vehicleType: vehicle.vehicleType || "",
    vin: vehicle.vin || "",
    serviceType: install.serviceType || "",
    installType: install.installType || install.serviceName || "",
    shades: install.shades || {},
    filmBrand: install.brand || install.brandSerieName || "",
    filmSeries: install.brandSerieName || install.filmSeries || "",
    bookingDate,
    bookingStartTime: install.startTime || "",
  };
}

/**
 * Build bookingPrefillFromLead-shaped object from store.state.customer.
 */
export function buildCustomerBookingPrefill(customer, options = {}) {
  const {
    isWarranty = false,
    warrantySource = null,
    relatedBookingId = null,
    relatedBooking = null,
  } = options;

  const prefill = {
    source: "customer",
    name: customer?.name ?? "",
    ph: customer?.phoneNumber ?? "",
    email: customer?.email ?? "",
    referral: customer?.referral ?? "",
    isWarranty: !!isWarranty,
    warrantySource: warrantySource || null,
    relatedBookingId: relatedBookingId ? String(relatedBookingId) : null,
  };

  if (relatedBooking) {
    const install = getBookingInstallDetails(relatedBooking);
    const vehicle = getBookingVehicleDetails(relatedBooking);
    prefill.vehicleBrand = vehicle.make ?? "";
    prefill.vehicleModel = vehicle.model ?? "";
    prefill.vehicleYear = vehicle.year ?? "";
    prefill.vehicleType = vehicle.vehicleType ?? install.vehicleType ?? "";
    prefill.quoteMode =
      install.serviceType === "ppf" ? "ppf" : install.serviceType === "multi" ? "multi" : "";
    if (install.installType) prefill.installType = String(install.installType);
    if (install.serviceName && !prefill.installType)
      prefill.installType = String(install.serviceName);
  } else if (customer?.bookings && typeof customer.bookings === "object") {
    let latest = null;
    let latestTime = 0;
    for (const [id, b] of Object.entries(customer.bookings)) {
      const d = parseBookingDate(b);
      const t = d ? d.getTime() : 0;
      if (t >= latestTime) {
        latestTime = t;
        latest = { id, booking: b };
      }
    }
    if (latest?.booking) {
      const install = getBookingInstallDetails(latest.booking);
      const vehicle = getBookingVehicleDetails(latest.booking);
      prefill.vehicleBrand = vehicle.make ?? "";
      prefill.vehicleModel = vehicle.model ?? "";
      prefill.vehicleYear = vehicle.year ?? "";
      prefill.vehicleType = vehicle.vehicleType ?? "";
      if (install.installType) prefill.installType = String(install.installType);
    }
  }

  return prefill;
}

export function clearBookingWarrantyContext() {
  store.state.bookingWarrantyContext = null;
  store.state.createFilmWarrantyForOriginalInstall = false;
  store.state.originalBookingWarrantyData = null;
}

const DEFAULT_CUSTOMER_PROFILE_CLOSE_GUARD_MS = 450;

/** Prevent Headless UI parent Dialog from closing when a child overlay (booking/warranty) dismisses. */
export function armCustomerProfileCloseGuard(ms = DEFAULT_CUSTOMER_PROFILE_CLOSE_GUARD_MS) {
  store.state.customerProfileCloseGuardUntil = Date.now() + ms;
}

export function isCustomerProfileCloseGuarded() {
  return Date.now() < (store.state.customerProfileCloseGuardUntil || 0);
}
